import { EnvironmentConfiguration } from './environment/services/environment.configuration';
import { Component, OnInit, ViewChild, NgZone, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationStore } from './permission/services/authentication.store';
import { Observable } from 'rxjs/Observable';
import { MenuStore } from './menus/service/menu.store';
import { PageScrollConfig } from 'ng2-page-scroll';
import { Angulartics2GoogleAnalytics } from 'angulartics2';
import { Router, NavigationEnd } from '@angular/router';
import { FlashMessagesComponent } from './shared/flash-messages/component/flash.messages.component';
import { User } from './auth/models/User';
import { ExpirationModalComponent } from './shared/expiration-modal/expiration-modal.component';
import { ExpirationModalConfig } from './shared/expiration-modal/expiration-modal-config';
import { Constants } from './shared/constants';
import { FlashMessage, MessageLevel } from './shared/flash-messages/flash.message.record';
import { LayoutService } from './services/layout.service';
import { Subscription } from 'rxjs/Subscription';
// cwr function declaration for Cloudwatch RUM
declare function cwr(operation: string, payload: any): void;
declare global {
  interface Window {
    DD_LOGS: any;
    DD_RUM: any;
  }
}
@Component({
  selector: 'pioneer-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {


  private showLayoutSubscription: Subscription;
  showLayout = true;

  @ViewChild(FlashMessagesComponent) flashMessageComponent: FlashMessagesComponent;
  @ViewChild(ExpirationModalComponent) expirationModal: ExpirationModalComponent;
  expirationModalConfig = new ExpirationModalConfig();

  user: User;
  userIsLoggedIn: Observable<boolean>;
  title: string;

  userRum: User = null;


  constructor(private translate: TranslateService,
              private authenticationStore: AuthenticationStore,
              menuStore: MenuStore,
              angulartics2GoogleAnalytics: Angulartics2GoogleAnalytics,
              public ngZone: NgZone,
              private router: Router,
              private layoutService: LayoutService,
              private environment: EnvironmentConfiguration) {
    // Configure the defaults and setup the translation service from ng-translate.
    translate.setDefaultLang('en-US');
    translate.use('en-US');

    // need to change duration, which otherwise would be 1250 milliseconds which feels very slow
    PageScrollConfig.defaultDuration = 0;

    this.userIsLoggedIn = authenticationStore.isLoggedIn();
    menuStore.pageTitle.subscribe(pageTitle => {
      // to remove the ExpressionChangedAfterItHasBeenCheckedError
      // https://github.com/angular/angular/issues/17572
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.ngZone.run(() => {
            this.title = pageTitle;
          });
        }, 0);
      });

    });


  }

  ngOnInit() {
    this.resetPage();
    this.authenticationStore.user.subscribe((user: User) => {
      this.user = user;
      this.createUserRum(user);
    });
    let authenticated = false;
    this.userIsLoggedIn.subscribe((loggedIn: boolean) => {
      authenticated = loggedIn;
    });
    // NgZone is used to run the interval because Protractor will wait forever for Observable.interval to finish
    // https://github.com/juliemr/ngconf-2016-zones/blob/master/src/app/main.ts#L88
    this.ngZone.runOutsideAngular(() => {
      setInterval(() => {
        this.ngZone.run(() => {
          let diff;
          if (this.user && this.user.expiration) {
            diff = Date.parse(this.user.expiration.toString()) - Date.parse(new Date().toString());
          } else {
            diff = 0;
          }
          const seconds = this.getSeconds(diff);
          if (seconds <= Constants.SESSION_EXPIRE_WARNING_SECONDS && authenticated) {
            if (this.expirationModal.closed) {
              this.expirationModal.open();
            }
            if (seconds <= 0 ) {
              this.expirationModal.close(false);
              this.authenticationStore.logout();
              this.expirationModal.setAlreadyExpired(true);
              this.expirationModalConfig.content = this.translate.instant('AUTHENTICATION_EXPIRATION.CONTENT_EXPIRED');
              const message = new FlashMessage(MessageLevel.ERROR, this.translate.instant('EXTERNAL_LOGIN_EXPIRED.LOG_IN_AGAIN'), true);
              this.flashMessageComponent.show(message);
            } else {
              this.expirationModal.setAlreadyExpired(false);
              this.expirationModalConfig.content = this.translate.instant('AUTHENTICATION_EXPIRATION.CONTENT_NOT_EXPIRED', {
                seconds: seconds
              });
            }
          }
        });
      }, 1000);
    });

    this.showLayoutSubscription = this.layoutService.showLayout.subscribe((showLayout: boolean) => {
      this.showLayout = showLayout;
    });

    this.setRumMonitor();
 }

  ngOnDestroy(): void {
    if (this.showLayoutSubscription) {
      this.showLayoutSubscription.unsubscribe();
    }
  }

  private setRumMonitor() {
     // Implement DataDog RUM
     if (this.environment.cwr.enable === true) {
      // Set user information
      if (this.userRum && this.userRum !== undefined && this.userRum !== null) {

        const email = this.userRum.email || '';
        const id = this.userRum.username || '';

        if (window && window.DD_RUM !== undefined) {
          window.DD_RUM.onReady(function() {
            window.DD_RUM.setUser({
                email,
                id
            });
          });
        }

        if (window && window.DD_LOGS !== undefined && typeof window.DD_LOGS.onReady === 'function') {
          window.DD_LOGS.onReady(function () {
            if (typeof window.DD_LOGS.setUser === 'function') {
              window.DD_LOGS.setUser({ email, id });
            }
          });
        }
      }

      // Set view name (User journey)
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          if (window.DD_RUM && typeof window.DD_RUM.startView === 'function') {
            window.DD_RUM.startView({name: this.router.url});
          }
        }
      });
    }

    // Disabling AWS rum
    /* if (this.environment.cwr.enable === true) {
     this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd && typeof cwr !== 'undefined') {
          cwr('recordPageView', this.router.url);
          if (this.userRum) {
            cwr('addSessionAttributes', { userName: this.userRum.email });
            cwr('recordEvent', {
              type: `user_custom_event_${this.userRum.email}`,
              data: {
                user: this.userRum,
                page: this.router.url
              }
            });
          }
        }
      });
    }*/
  }

  private getSeconds(t) {
    return Math.max(Math.floor(t / 1000), 0);
  }

  private createUserRum(user) {
    if (user && user.email && user.email.length > 0 && this.userRum === null) {
      const userEmail =  this.checksum(user.email);
      const userUserName = this.checksum(user.username);
      const fistName = this.checksum(user.firstName);
      const lastName = this.checksum(user.lastName);
      const newUser = JSON.parse(JSON.stringify(user));

      newUser.email = userEmail;
      newUser.fistName = fistName;
      newUser.username = userUserName;
      newUser.lastName = lastName;
      this.userRum = newUser;
    }
  }
  /**
   * Checks to see if navigation was successful and resets the page
   * to an expected state.
   */
  resetPage() {
    this.router.events.subscribe((routerEvent) => {
      if (!(routerEvent instanceof NavigationEnd)) {
        return;
      }
      /**
       * Forces the app to scroll to the top of page when navigation is successful,
       * solving the issue where the user would change pages and stay in the middle
       * of the page.
       * See: https://stackoverflow.com/questions/39601026/angular-2-scroll-to-top-on-route-change
       */
      window.scrollTo(0, 0);

      /**
       * Resets any flash Messages that aren't set to be persistent.
       */
      this.flashMessageComponent.clearNonPersistentMessages();
    });
  }

  padStart(str: string, targetLength: number, padString: string): string {
      targetLength = Math.floor(targetLength);
      padString = String(typeof padString !== 'undefined' ? padString : ' ');
      if (str.length >= targetLength) {
          return String(str);
      } else {
          targetLength = targetLength - str.length;
          if (targetLength > padString.length) {
              padString += padString.repeat(targetLength / padString.length);
          }
          return padString.slice(0, targetLength) + String(str);
      }
  }

  checksum(str: string): string {
    const fnv_prime = 0x01000193;
    let hash = 0x811c9dc5;

    for (let i = 0; i < str.length; i++) {
        let charCode = str.charCodeAt(i);
        let hashBinary = this.padStart((hash).toString(2), 32, '0').split('');
        let charCodeBinary = this.padStart((charCode).toString(2), 32, '0').split('');

        for (let j = 0; j < 32; j++) {
            hashBinary[j] = hashBinary[j] === charCodeBinary[j] ? '0' : '1';
        }
        hash = parseInt(hashBinary.join(''), 2);

        hash = Math.floor(hash * fnv_prime) % 4294967296;
    }
    let fnv = hash.toString(16);
    if (fnv.length > 8) {
        fnv = fnv.substring(0, 8);
    }
    return fnv;
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/app.component.ts